import React from 'react';
import { Flag } from 'lucide-react';
import Button from '../../common/Button';

/**
 * Plan 05b-05 / STAFF-06 — RaiseFindingButton.
 * Constants block patched in 06-05 (FIND-02) per CONTEXT D5 — closes 05c-08 MISS-1.
 *
 * Small flag-icon button mounted per response row's action cluster. Opens the
 * ObservationModal in the parent component when clicked. Inline-only — no
 * label text. Tooltip on hover communicates intent.
 *
 * UAT FIX D/F (2026-06-19): restyled to MATCH the ResponseShell communication
 * buttons — icon-only `Button variant="secondary-outline"` wrapped in a
 * position-relative span that carries the corner finding-count badge
 * (`response-action-count--corner`), exactly like the internal/external comm
 * buttons. The public API (onClick / disabled / title / count) is unchanged so
 * every call site compiles without edits. The finding-count corner badge also
 * doubles as the click-to-view affordance — there is no separate FindingBadge.
 *
 * Polymorphic item_type_id constants point at LIVE UTL_entity_registry rows.
 * After migration 076 seeded SAMPLE_ITEM + AUDIT_RESPONSE, all 6 transactional
 * response types share a single 'AUDIT_RESPONSE' registry row (per D5 —
 * differentiation lives in procedure_instance_id + item_record_id already).
 * The 3 process-audit constants (ITEM_TYPE_PROCESS_STEP / RISK / CONTROL) point
 * at the pre-existing 051-seeded registry rows.
 */

// Live UTL_entity_registry ids (seeded by migration 076 — see 06-CONTEXT D5).
// All 6 transactional response constants point at the SAME 'AUDIT_RESPONSE' row;
// response-type differentiation lives in procedure_instance_id + item_record_id
// already, so a single polymorphic id covers all 6 transactional types per D5.
//   SELECT id, type_code FROM UTL_entity_registry
//   WHERE type_code IN ('SAMPLE_ITEM','AUDIT_RESPONSE');
//   →  29=SAMPLE_ITEM, 30=AUDIT_RESPONSE
// Verified against live dev DB 2026-05-18 (06-01 DBA-apply checkpoint).
export const ITEM_TYPE_CHECKLIST_RESPONSE      = 30;
export const ITEM_TYPE_QUESTIONNAIRE_RESPONSE  = 30;
export const ITEM_TYPE_FORM_RESPONSE           = 30;
export const ITEM_TYPE_RATING_RESPONSE         = 30;
export const ITEM_TYPE_CONFIRMATION_RESPONSE   = 30;
export const ITEM_TYPE_PHYSICAL_CHECK_RESPONSE = 30;
export const ITEM_TYPE_SAMPLE_ITEM             = 29;

// === Phase 05c — STAFF-11 polymorphic finding link targets ===
// The 05c process-audit forms link findings to the SOURCE-ROW id (process_step_id /
// risk_id / control_id), NOT to the response_id, per CONTEXT D5. Numeric ids
// correspond to UTL_entity_registry.type_code (seeded by migration 051).
// Verified against live dev DB 2026-05-18 — 05c-08 MISS-1 patched.
//   SELECT id, type_code FROM UTL_entity_registry
//   WHERE type_code IN ('SOP_STEP','RISK','CONTROL');
//   →  4=SOP_STEP, 9=RISK, 11=CONTROL
export const ITEM_TYPE_PROCESS_STEP            = 4;  // 05c — Walkthrough finding link (UTL_entity_registry.SOP_STEP)
export const ITEM_TYPE_RISK                    = 9;  // 05c — RiskAssessment finding link (UTL_entity_registry.RISK)
export const ITEM_TYPE_CONTROL                 = 11; // 05c — ControlTest finding link (UTL_entity_registry.CONTROL)

export interface RaiseFindingButtonProps {
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  /** Finding count for this row — rendered as a corner notification badge on the
   *  same flag icon (no separate FindingBadge element). */
  count?: number;
  /** Per-row test id; defaults to BTN_raise_finding. Forwarded to the button so
   *  body-specific ids (BTN_walkthrough_raise_finding_<id>, …) are preserved. */
  'data-testid'?: string;
  /**
   * Whether to show a numeric count badge on the flag icon.
   * Defaults true (preserves existing callers). Pass false from inline action
   * clusters (Phase 113 REQ-7) to suppress the badge.
   */
  showCount?: boolean;
}

const RaiseFindingButton: React.FC<RaiseFindingButtonProps> = ({
  onClick,
  disabled = false,
  title = 'Raise a finding from this row',
  count = 0,
  'data-testid': testId = 'BTN_raise_finding',
  showCount = true,
}) => {
  const label = count > 0 ? `${count} finding${count === 1 ? '' : 's'} — raise another` : title;
  return (
    <span className="response-comm-action response-raise-finding">
      <Button
        variant="secondary-outline"
        icon={Flag}
        data-testid={testId}
        className="raise-finding-btn--icon"
        onClick={onClick}
        disabled={disabled}
        title={label}
        aria-label={label}
      />
      {showCount && count > 0 && (
        <span className="response-action-count response-action-count--corner response-action-count--finding">
          {count}
        </span>
      )}
    </span>
  );
};

export default RaiseFindingButton;
